/**
 * Dicionário Didático de Status e Rejeições da SEFAZ (MOC 7.0)
 * Padrão Material Design 3 e público 40/50+: Linguagem clara, técnica, sem jargões indecifráveis.
 */

export interface SefazDiagnosis {
  cStat: string;
  title: string;
  whatHappened: string;
  taxImpact: string;
  solution: string;
  canRetry: boolean;
}

export function getSefazDiagnosis(
  cStatInput?: string | number | null,
  motivoStatus?: string | null
): SefazDiagnosis {
  const cStat = String(cStatInput || '').trim();
  const motivo = String(motivoStatus || '').trim();

  const isRegularized = motivo.includes('[Regularizada');
  if (isRegularized) {
    const match = motivo.match(/\[(Regularizada na Nota Nº [^\]]+)\]/);
    const regText = match ? match[1] : 'Regularizada em nova nota fiscal';
    return {
      cStat: cStat || 'REG',
      title: 'Nota Fiscal Rejeitada Regularizada',
      whatHappened: `A inconsistência inicial desta tentativa foi solucionada e a venda foi transmitida com sucesso (${regText}).`,
      taxImpact: 'Este registro rejeitado permanece arquivado apenas para fins de histórico e auditoria contábil, sem qualquer débito de impostos.',
      solution: 'Não há mais pendências. A nova nota fiscal oficial já se encontra autorizada na SEFAZ e disponível na listagem.',
      canRetry: false,
    };
  }

  switch (cStat) {
    case '252':
      return {
        cStat: '252',
        title: 'Canal de Envio Divergente (Homologação vs. Produção)',
        whatHappened:
          'A nota foi gerada com identificador de ambiente oficial (Produção), mas o canal de comunicação estava conectado ao servidor de testes (Homologação) durante a transição.',
        taxImpact:
          'Nenhum imposto foi gerado nem recolhido. Esta tentativa não possui qualquer validade jurídica ou fiscal perante o Fisco.',
        solution:
          'O canal oficial de Produção está pronto. Clique em "Reemitir com Mesmo Nº" para autorizar a nota oficial com o mesmo número fiscal.',
        canRetry: true,
      };

    case '245':
      return {
        cStat: '245',
        title: 'CNPJ do Estabelecimento Não Habilitado no Canal',
        whatHappened:
          'A SEFAZ recebeu a assinatura digital do Certificado A1, mas o CNPJ ainda não consta liberado para emissão no ambiente selecionado (ou falta o credenciamento de NFC-e).',
        taxImpact:
          'A venda não foi autorizada fiscalmente. Não houve cálculo ou retenção de tributos.',
        solution:
          'Se a empresa opera oficialmente, certifique-se de que o seletor está em "Produção (Oficial)". Caso seja nova, confirme com o contador a habilitação do Modelo 65 no portal estadual.',
        canRetry: true,
      };

    case '391':
      return {
        cStat: '391',
        title: 'Identificação de Pagamento Eletrônico (Cartão / PIX)',
        whatHappened:
          'A SEFAZ exige a especificação do tipo de integração nas formas de pagamento eletrônicas (Cartão de Crédito, Débito ou PIX), segundo a NT 2020.006.',
        taxImpact:
          'A nota não foi homologada. Nenhum débito fiscal foi registrado.',
        solution:
          'O R-MAX normaliza automaticamente para "Operação não integrada (maquininha avulsa)". Clique em "Reemitir com Mesmo Nº" para transmitir novamente.',
        canRetry: true,
      };

    case '204':
    case '539':
      return {
        cStat,
        title: 'Duplicidade de Nota Fiscal na SEFAZ',
        whatHappened:
          'Esta venda já foi transmitida e aprovada pela SEFAZ anteriormente sob este mesmo número e série fiscal.',
        taxImpact:
          'A nota fiscal já é oficial e válida. O imposto correspondente já está assegurado no documento original.',
        solution:
          'Não é necessário emitir novamente. O sistema já recupera a chave de acesso e o protocolo oficial da nota autorizada.',
        canRetry: false,
      };

    case '629':
      return {
        cStat: '629',
        title: 'Arredondamento de Centavos no Total do Item',
        whatHappened:
          'A multiplicação entre a quantidade fracionada e o valor unitário divergiu em centavos do total discriminado no item.',
        taxImpact:
          'A nota não foi aceita pela SEFAZ. Não há impacto tributário gerado.',
        solution:
          'O motor de cálculo do R-MAX já realizou a reconciliação matemática. Clique em "Reemitir NFC-e" para validar.',
        canRetry: true,
      };

    case '508':
      return {
        cStat: '508',
        title: 'Código de Tributação (CST/CSOSN) Incompatível',
        whatHappened:
          'O código de situação tributária informado em um dos itens não é aceito para operações diretas com o consumidor final.',
        taxImpact:
          'Nota rejeitada e cancelada no processamento. Sem incidência tributária.',
        solution:
          'Verifique se os itens do Simples Nacional estão classificados com CSOSN 102 (Tributada) ou 500 (Substituição Tributária) antes de reemitir.',
        canRetry: true,
      };

    case '225':
      return {
        cStat: '225',
        title: 'Formato de Lote Não Reconhecido (Schema XML)',
        whatHappened:
          'Algum campo obrigatório continha caracteres especiais ou dados fora do padrão exigido pelo manual do contribuinte da SEFAZ.',
        taxImpact:
          'Sem valor fiscal e sem retenção tributária.',
        solution:
          'Verifique o cadastro de produtos ou endereço da empresa e clique em "Reemitir com Mesmo Nº".',
        canRetry: true,
      };

    case 'INUT_EXTINTA':
    case '422':
      return {
        cStat: 'SINIEF',
        title: 'Inutilização Extinta pelo CONFAZ (Ajustes nº 40/2026 e 38/2026)',
        whatHappened:
          'A partir de 01/11/2026, a SEFAZ-SP (além de BA e AC) extinguiu o Pedido de Inutilização de Número da NF-e e NFC-e.',
        taxImpact:
          'Sem penalidades fiscais. A SEFAZ não exige nem recebe mais eventos de inutilização.',
        solution:
          'O R-MAX retransmite notas rejeitadas com o mesmo número fiscal. Em caso de salto definitivo, mantenha apenas no controle interno para a contabilidade.',
        canRetry: false,
      };

    default:
      return {
        cStat: cStat || 'REJ',
        title: cStat ? `Rejeição SEFAZ (Código ${cStat})` : 'Rejeição na Transmissão Fiscal',
        whatHappened:
          motivo ||
          'A Secretaria da Fazenda identificou inconformidade em um dos parâmetros enviados e não autorizou o documento.',
        taxImpact:
          'Notas rejeitadas não possuem validade fiscal nem geram cobrança de impostos.',
        solution:
          'Revise os dados fiscais da venda e clique em "Reemitir com Mesmo Nº" para transmitir mantendo a sequência oficial (Ajuste SINIEF nº 40/2026).',
        canRetry: true,
      };
  }
}
